import clsx from 'clsx';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import Switch from '../../../../components/Switch';
import usePoolState from '../../../../context/pool/usePoolState';
import DepositOverview from './DepositOverview';
import { useReducer } from 'react';
import { TokenIcons } from '../../../../assets/icons';
// import useUserLockUps from '../../../../hooks/web3/pools/useUserLockUps';
import useMockUserLockUps from '../../../../hooks/web3/pools/useMockUserLockUps';
import Spinner from '../../../../components/Spinner';
import {
    CHAIN_INFO,
    EnabledChains,
    INSUFFICIENT_BALANCE,
    NO_LOCK_EARLY_WITHDRAWAL_TIME,
    TRANSACTION_PENDING,
    TimeConversions,
    Tokens,
    Validators,
} from '../../../../config/constants';
import { toBigNumber } from '../../../../web3/utils';
import { parseEther, parseUnits } from 'ethers/lib/utils';
import { validateErrors, verifyChainConfig } from '../../../../helpers';
import useToastState from '../../../../context/toast/useToastState';
import useModalState from '../../../../context/modal/useModalState';
import { ILiquidityPool__factory } from '../../../../config/contracts/typechain';
import { useWeb3React } from '@web3-react/core';
import { useSWRConfig } from 'swr';
import useErrorState from '../../../../context/error/useErrorState';
// import useUserLockedDeposits from '../../../../hooks/web3/pools/useUserLockedDeposits';
import useMockUserLockedDeposits from '../../../../hooks/web3/pools/useMockUserLockedDeposits';
import { BigNumber } from 'ethers';
import EarlyWithdrawalPenalty from './EarlyWithdrawalPenalty';
import moment from 'moment';
// import useUserTotalDeposits from '../../../../hooks/web3/pools/useUserTotalDeposits';
import useMockUserTotalDeposits from '../../../../hooks/web3/pools/useMockUserTotalDeposits';
import useUserPoolPnl from '../../../../hooks/web3/pools/useUserPoolPnl';
// import useUserTotalWithdrawals from '../../../../hooks/web3/pools/useUserTotalWithdrawals';
import useMockUserTotalWithdrawals from '../../../../hooks/web3/pools/useMockUserTotalWithdrawals';

const PoolDepositOverview = () => {
    const {
        selectedLiquidityPool,
        selectedLockUp,
        withdrawAmount,
        renderWithdrawInterface,
        toggleWithdrawInterface,
    } = usePoolState();
    const [showUsdc, toggleUsdc] = useReducer((prev) => !prev, false);

    const { library, chainId, account } = useWeb3React();

    const { setError } = useErrorState();

    const { openModal, setModalData, closeModal } = useModalState();

    const { mutate: globalMutate } = useSWRConfig();

    const { setToast } = useToastState();

    // const { updateUserLockedDeposits } = useUserLockedDeposits();
    const { updateUserLockedDeposits } = useMockUserLockedDeposits();

    // const { userTotalDeposits } = useUserTotalDeposits();
    const { userTotalDeposits } = useMockUserTotalDeposits();

    // const {
    //     userLockUps,
    //     isLoading: isUserLockUpLoading,
    //     updateUserLockUps,
    // } = useUserLockUps();
    const {
        userLockUps,
        isLoading: isUserLockUpLoading,
        updateUserLockUps,
    } = useMockUserLockUps();

    // const { updateUserTotalWithdrawals } = useUserTotalWithdrawals();
    const { updateUserTotalWithdrawals } = useMockUserTotalWithdrawals();
    const { updateUserPoolPnl } = useUserPoolPnl();

    const selectedWithdrawalPool =
        userLockUps && selectedLiquidityPool
            ? userLockUps[selectedLiquidityPool.poolId].details.find(
                  ({ lockUp }) => lockUp?.duration === selectedLockUp.duration
              )
            : undefined;

    const noLockUp =
        selectedLiquidityPool &&
        userLockUps &&
        userLockUps[selectedLiquidityPool.poolId].details.find(
            ({ lockUp }) => lockUp?.duration === undefined
        );

    const earlyWithdrawPenalty =
        selectedLiquidityPool &&
        userLockUps &&
        noLockUp &&
        parseUnits(noLockUp.lockedLp, selectedLiquidityPool.decimals).gt(0) &&
        userLockUps[selectedLiquidityPool.poolId].lastDepositTimestamp +
            NO_LOCK_EARLY_WITHDRAWAL_TIME * TimeConversions.SECONDS_PER_HOUR >
            moment().unix();

    const validators: Validators[] = [
        {
            errorCondition: toBigNumber(withdrawAmount || '0').isZero(),
            message: 'Amount is required!',
        },
        {
            errorCondition: parseUnits(
                withdrawAmount || '0',
                selectedLiquidityPool?.decimals
            ).gt(
                parseUnits(
                    selectedWithdrawalPool?.unlockedLp || '0',
                    selectedLiquidityPool?.decimals
                )
            ),
            message: INSUFFICIENT_BALANCE,
        },
    ];

    const onConfirm = async () => {
        try {
            if (!selectedLiquidityPool) throw new Error('No selected locked pool');
            if (!selectedWithdrawalPool) throw new Error('No Selected Withdrawal Pool');
            if (!verifyChainConfig(chainId)) return;
            if (
                !validateErrors(validators, (message) =>
                    setToast(
                        {
                            type: 'danger',
                            message,
                        },
                        message
                    )
                )
            ) {
                return;
            }

            openModal('waitingModal');
            setModalData((prev) => ({ ...prev, status: TRANSACTION_PENDING }));

            const lpContract = ILiquidityPool__factory.connect(
                selectedLiquidityPool.poolId,
                library.getSigner()
            );

            const totalShares = BigNumber.from(selectedWithdrawalPool.totalPoolShares);

            const percentageToWithdraw = parseUnits(
                withdrawAmount,
                selectedLiquidityPool?.decimals
            )
                .mul(parseUnits('1', selectedLiquidityPool?.decimals))
                .div(
                    parseUnits(
                        selectedWithdrawalPool.unlockedLp,
                        selectedLiquidityPool?.decimals
                    )
                );

            const sharesToWithdraw = totalShares
                .mul(percentageToWithdraw)
                .div(parseUnits('1', selectedLiquidityPool?.decimals));

            // TODO minout value;

            const deposit = await (selectedLockUp.lockUpId !== undefined
                ? lpContract.withdrawFromPool(
                      selectedLockUp.lockUpId,
                      sharesToWithdraw.toString(),
                      0
                  )
                : lpContract.withdraw(
                      parseUnits(withdrawAmount, selectedLiquidityPool?.decimals),
                      0
                  ));

            const receipt = await deposit.wait();

            // attempt to update chart after 3 seconds
            setTimeout(() => {
                globalMutate([
                    'historicalLockedPoolLiquidity',
                    chainId,
                    selectedLiquidityPool.poolId,
                ]);
            }, 3000);

            await updateUserLockUps();
            await updateUserLockedDeposits();
            await updateUserTotalWithdrawals();
            await updateUserPoolPnl();

            setToast(
                {
                    type: 'success',
                    message: (
                        <>
                            USDC Withdrawn!&nbsp;
                            <Button
                                variant='transparent'
                                externalLink={`${
                                    CHAIN_INFO[chainId as EnabledChains[number]]
                                        ?.blockExplorer
                                }/tx/${receipt.transactionHash}`}
                            >
                                View TX
                            </Button>
                        </>
                    ),
                },
                `${selectedLiquidityPool.poolId}-${withdrawAmount}`
            );

            toggleWithdrawInterface();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return selectedLiquidityPool ? (
        <Card className='pool-deposit-overview'>
            {account ? (
                !isUserLockUpLoading && userTotalDeposits ? (
                    parseEther(userTotalDeposits.totalDepositAssetAllPools).isZero() ? (
                        <div className='no-deposits'>
                            You have not deposited in any pool yet
                        </div>
                    ) : userLockUps && Object.keys(userLockUps) ? (
                        <>
                            {renderWithdrawInterface &&
                            !selectedLockUp.duration &&
                            earlyWithdrawPenalty ? (
                                <EarlyWithdrawalPenalty />
                            ) : (
                                <>
                                    <div className='lp-token'>
                                        <span>
                                            Show{' '}
                                            {showUsdc
                                                ? Tokens.USDC
                                                : selectedLiquidityPool.lpToken}{' '}
                                            Amounts
                                        </span>
                                        <Switch
                                            checked={showUsdc}
                                            onToggle={toggleUsdc}
                                            renderThumb={(isChecked) => (
                                                <span
                                                    className={clsx({
                                                        'is-checked': isChecked,
                                                    })}
                                                >
                                                    {
                                                        TokenIcons[
                                                            selectedLiquidityPool.lpToken
                                                        ]
                                                    }
                                                </span>
                                            )}
                                        />
                                    </div>
                                    <div className='deposit-overview'>
                                        <div className='row header'>
                                            <span>Type</span>
                                            <span>Unlocked {showUsdc && '(USDC)'}</span>
                                            <span>Locked {showUsdc && '(USDC)'}</span>
                                        </div>
                                        {userLockUps[
                                            selectedLiquidityPool.poolId
                                        ]?.details.map((lockPoolDetail, index) => (
                                            <DepositOverview
                                                key={index}
                                                lockPoolDetail={lockPoolDetail}
                                                showUsdc={showUsdc}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                            <div className='buttons-container'>
                                {renderWithdrawInterface ? (
                                    <>
                                        <Button onClick={onConfirm}>
                                            Confirm <span>&nbsp;USDC Withdrawal</span>
                                        </Button>
                                        <Button
                                            variant='secondary'
                                            onClick={toggleWithdrawInterface}
                                        >
                                            Back
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant='secondary'
                                        onClick={toggleWithdrawInterface}
                                    >
                                        Withdraw
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className='error-container'>
                            We had trouble loading your info
                        </div>
                    )
                ) : (
                    <div className='loading-container'>
                        <Spinner />
                        <span>Loading Withdraw Stats...</span>
                    </div>
                )
            ) : (
                <div className='not-connected'>
                    Connect your wallet to see your pool balances
                </div>
            )}
        </Card>
    ) : (
        <Card className='card pool-deposit-overview'>
            <div className='loading-container'>
                <Spinner />
            </div>
        </Card>
    );
};

export default PoolDepositOverview;
